/**
 * @file schemas.ts
 * @brief Zod schemas for Company Admin → Schedule (diary).
 *
 * @author DES
 * @date 19-06-2026
 */

import { z } from "zod";

export const ScheduledSessionStatusSchema = z.enum([
  "pending",
  "reschedule_requested",
  "cancelled",
  "missed",
  "cancelled_permanently",
  "deleted",
]);

export type ScheduledSessionStatus = z.infer<
  typeof ScheduledSessionStatusSchema
>;

export const ScheduledSessionSchema = z
  .object({
    id: z.string(),
    company_id: z.string().nullable().optional(),
    scheduler_id: z.string().nullable().optional(),
    participant_id: z.string().nullable().optional(),
    participant_email: z.string(),
    person_b_name: z.string().nullable().optional(),
    reference_number: z.string().nullable().optional(),
    person_a_gender: z.string().nullable().optional(),
    person_b_gender: z.string().nullable().optional(),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    scheduled_at: z.string(),
    status: ScheduledSessionStatusSchema.or(z.string()),
    participant_first_name: z.string().nullable().optional(),
    participant_last_name: z.string().nullable().optional(),
    scheduler_first_name: z.string().nullable().optional(),
    scheduler_last_name: z.string().nullable().optional(),
  })
  .passthrough();

export type ScheduledSession = z.infer<typeof ScheduledSessionSchema>;

export const ScheduledSessionsResponseSchema = z.object({
  success: z.boolean().optional(),
  sessions: z.array(ScheduledSessionSchema),
});

export const ScheduleSessionInputSchema = z.object({
  participantId: z.string().min(1, "Select an employee"),
  participantEmail: z.string().email(),
  person_b_name: z.string().optional(),
  reference_number: z.string().optional(),
  person_a_gender: z.string().optional(),
  person_b_gender: z.string().optional(),
  sourceLang: z.string().min(1, "Source language is required"),
  targetLang: z.string().min(1, "Target language is required"),
  scheduledAt: z.string().min(1, "Date and time are required"),
});

export type ScheduleSessionInput = z.infer<typeof ScheduleSessionInputSchema>;

export const S2SLanguagesResponseSchema = z.object({
  success: z.boolean().optional(),
  languages: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      locale: z.string().optional(),
    }),
  ),
});

export type ScheduleView = "upcoming" | "all" | "past";

export interface ScheduleListParams {
  q?: string;
  status?: ScheduledSessionStatus;
  employee_id?: string;
  view?: ScheduleView;
}
